import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import { useAuth } from '../contexts/AuthContext';

const INK = '#14261F';
const PRIMARY = '#1E5C46';
const PRIMARY_STRONG = '#174A38';
const ACCENT = '#D9A441';
const MUTED = '#66736C';
const BORDER = '#E3E8E3';
const BG = '#F6F8F6';
const SHADOW = '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)';

export default function Home() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        bgcolor: BG,
      }}
    >
      {/* Brand plane — matches Login */}
      <Box
        sx={{
          bgcolor: INK,
          color: '#F3F6F4',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: { xs: 3.5, md: 6 },
          minHeight: { xs: 220, md: '100vh' },
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at 20% 80%, rgba(30,92,70,0.45) 0%, transparent 55%), radial-gradient(ellipse at 90% 10%, rgba(217,164,65,0.12) 0%, transparent 40%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Typography
          sx={{
            position: 'relative',
            zIndex: 1,
            fontFamily: '"Archivo", sans-serif',
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: '0.14em',
          }}
        >
          ATU
          <Box component="span" sx={{ color: ACCENT }}>
            ·
          </Box>
          TEIN
        </Typography>

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Archivo", sans-serif',
              fontWeight: 600,
              fontSize: { xs: 28, md: 40 },
              lineHeight: 1.15,
              maxWidth: 440,
              letterSpacing: '-0.01em',
            }}
          >
            One registry.
            <br />
            Every member, record and election.
          </Typography>
          <Typography
            sx={{
              color: '#A8B5AE',
              mt: 2,
              maxWidth: 400,
              fontSize: 15,
              lineHeight: 1.55,
            }}
          >
            Choose a portal to continue — staff manage the registry; members
            access their account, votes and updates.
          </Typography>
        </Box>

        <Typography sx={{ position: 'relative', zIndex: 1, fontSize: 12, color: '#6F7E76' }}>
          © 2026 ATUTEIN · Accra, Ghana
        </Typography>
      </Box>

      {/* Portal choices */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 5 },
          bgcolor: '#FFFFFF',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Archivo", sans-serif',
              fontWeight: 600,
              fontSize: 24,
              mb: 0.75,
              letterSpacing: '-0.01em',
              color: INK,
            }}
          >
            Welcome
          </Typography>
          <Typography sx={{ color: MUTED, mb: 3.5, fontSize: 14 }}>
            Sign in with the portal that matches your role.
          </Typography>

          <Box
            role="button"
            tabIndex={0}
            onClick={() => navigate('/login')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/login');
              }
            }}
            sx={{
              mb: 2,
              p: 2.5,
              borderRadius: '10px',
              border: `1px solid ${BORDER}`,
              boxShadow: SHADOW,
              cursor: 'pointer',
              transition: 'border-color .15s, background .15s',
              '&:hover': { borderColor: PRIMARY, bgcolor: '#F6F8F6' },
              '&:focus-visible': {
                outline: `2px solid ${ACCENT}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  bgcolor: PRIMARY,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AdminPanelSettingsIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: '"Archivo", sans-serif',
                    fontWeight: 600,
                    fontSize: 16,
                    color: INK,
                    mb: 0.35,
                  }}
                >
                  Admin portal
                </Typography>
                <Typography sx={{ color: MUTED, fontSize: 13.5, lineHeight: 1.45 }}>
                  Manage members, HR, elections, reports and operations.
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                navigate('/login');
              }}
              sx={{
                bgcolor: PRIMARY,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                py: 1.1,
                boxShadow: 'none',
                '&:hover': { bgcolor: PRIMARY_STRONG, boxShadow: 'none' },
              }}
            >
              Staff sign in
            </Button>
          </Box>

          <Box
            sx={{
              p: 2.5,
              borderRadius: '10px',
              border: `1px solid ${BORDER}`,
              boxShadow: SHADOW,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  bgcolor: INK,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PeopleIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: '"Archivo", sans-serif',
                    fontWeight: 600,
                    fontSize: 16,
                    color: INK,
                    mb: 0.35,
                  }}
                >
                  Member portal
                </Typography>
                <Typography sx={{ color: MUTED, fontSize: 13.5, lineHeight: 1.45 }}>
                  View events, vote, update your profile and get announcements.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.25 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate('/member-portal/login')}
                sx={{
                  borderColor: BORDER,
                  color: INK,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  py: 1.1,
                  '&:hover': { bgcolor: BG, borderColor: BORDER },
                }}
              >
                Login
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/member-portal/register')}
                sx={{
                  bgcolor: PRIMARY,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  py: 1.1,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: PRIMARY_STRONG, boxShadow: 'none' },
                }}
              >
                Register
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
